import React from 'react';
import { Link, useHistory, useLocation, useParams, Redirect } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import {
  StyledPageWrapper,
  StyledHeader,
  StyledLeftHeaderSection,
  StyledDarkSection,
  StyledDetailsContainer,
  StyledSidebarContainer,
  StyledMainContainer,
  StyledPostTitle,
  StyledSectionHeading,
  StyledParagraph,
} from './styled';
import { ReactComponent as LOGO } from '../../assets/img/tol-logo.svg';
import { ReactComponent as LOGOMobile } from '../../assets/img/tol-logo-mobile.svg';
import Seo from '../../components/Seo';
import { BLOG_POSTS, BLOG_SLUGS, PAGE_SEO, blogPostBySlug } from '../../service/seoMeta';
import { getBlogArticle } from './articles';

const USER_GUIDE_LINKS = [
  { to: '/user-guide/start', label: 'Getting started' },
  { to: '/user-guide/players', label: 'Players and squad' },
  { to: '/user-guide/off-season', label: 'Off-season' },
  { to: '/user-guide/trading', label: 'Trading and transfers' },
  { to: '/user-guide/competitions', label: 'Competitions' },
  { to: '/user-guide/coaching', label: 'Coaching' },
  { to: '/user-guide/finances', label: 'Club finances' },
];

function BlogShell({ children }) {
  return (
    <StyledPageWrapper>
      <StyledDarkSection>
        <StyledHeader>
          <StyledLeftHeaderSection>
            <Link to="/" aria-label="The Open League home">
              {isMobile ? <LOGOMobile /> : <LOGO />}
            </Link>
          </StyledLeftHeaderSection>
        </StyledHeader>
      </StyledDarkSection>
      <StyledDetailsContainer>
        <StyledSidebarContainer />
        <StyledMainContainer>{children}</StyledMainContainer>
      </StyledDetailsContainer>
    </StyledPageWrapper>
  );
}

function BlogIndex() {
  return (
    <BlogShell>
      <Seo {...PAGE_SEO.blog} />
      <StyledPostTitle>From the Blog</StyledPostTitle>
      <ul>
        {BLOG_POSTS.map((post) => (
          <li key={post.slug} style={{ marginBottom: '1rem' }}>
            <Link to={`/blog/${post.slug}`}>
              <strong>{post.title}</strong>
            </Link>
            <div>{post.description}</div>
          </li>
        ))}
      </ul>
      <StyledSectionHeading as="h2">
        New to TOL? Read the user guide
      </StyledSectionHeading>
      <ul>
        {USER_GUIDE_LINKS.map((guide) => (
          <li key={guide.to} style={{ marginBottom: '0.5rem' }}>
            <Link to={guide.to}>{guide.label}</Link>
          </li>
        ))}
      </ul>
      <StyledParagraph big>
        <Link to="/user-guide/start">User guide</Link>
        {' · '}
        <Link to="/press-kit">Press kit</Link>
      </StyledParagraph>
    </BlogShell>
  );
}

function BlogPost() {
  const { slug } = useParams();
  const post = blogPostBySlug(slug);
  const Article = getBlogArticle(slug);

  if (!post || !Article) {
    return <Redirect to="/blog" />;
  }

  return (
    <BlogShell>
      <Seo
        title={`${post.title} | The Open League`}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
      />
      <p>
        <Link to="/blog">← Back to blog</Link>
      </p>
      <Article />
    </BlogShell>
  );
}

/**
 * Public blog routes: /blog index and /blog/:slug posts.
 * /blog#slug redirects to /blog/slug. Logged-in users are not sent to /home.
 */
export default function Blog() {
  const history = useHistory();
  const { hash } = useLocation();
  const { slug } = useParams();

  React.useEffect(() => {
    if (!slug && hash) {
      const id = hash.replace(/^#/, '');
      if (BLOG_SLUGS.includes(id)) {
        history.replace(`/blog/${id}`);
      }
    }
  }, [hash, history, slug]);

  if (slug) {
    return <BlogPost />;
  }

  return <BlogIndex />;
}
